// FILE: hooks/useCountUp.ts
"use client";
import { useEffect, useRef, useState } from "react";

/**
 * Count-up animation với easing (easeOutExpo).
 * @param endValue  Giá trị đích
 * @param duration  Thời gian (ms), default 1200
 * @param startFrom Giá trị bắt đầu (default 0). Dùng cho stale-while-revalidate.
 */
export function useCountUp(endValue: number, duration = 1200, startFrom = 0) {
  const [value, setValue] = useState(startFrom);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (endValue === startFrom) {
      setValue(endValue);
      return;
    }

    startTimeRef.current = null;
    const from = startFrom;
    const delta = endValue - from;

    const tick = (ts: number) => {
      if (startTimeRef.current === null) startTimeRef.current = ts;
      const elapsed = ts - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo: nhanh đầu, chậm cuối → cảm giác "tiền đổ về"
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setValue(Math.round(from + delta * eased));

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endValue, duration]);

  return value;
}